import { atom, selector } from 'recoil'
import { sortBy } from 'lodash'
import { keyFactory } from 'src/utils/recoil'
import { convertDateToString, getDateWithoutBloodyTZ, localDate } from 'src/utils/date-utils'
import {
  CustomFootfall,
  YYYYMMDDDateString,
  CustomFootfallRequest,
  FootTrafficTrends,
  TRAFFIC_TRENDS_DAY_ID,
  TrendsDatum,
} from 'src/common-services'
import { CustomFootFallDateRange } from 'src/atoms/custom-polygon'

import * as customPolygonAtoms from 'src/atoms/custom-polygon'

const key = keyFactory('custom-polygon-foot-traffic-trends')

const BEGINNING_OF_TIME = `2019-01-01` as YYYYMMDDDateString
const TODAY = convertDateToString(new Date()) as YYYYMMDDDateString
// 2 years ago - present
export const ALL_TIME = {
  start: BEGINNING_OF_TIME,
  end: TODAY,
}

export const dateRange = atom<CustomFootFallDateRange>({
  key: key('date-range'),
  default: ALL_TIME,
})

export const aggregation = atom<'day' | 'week' | 'month'>({
  key: key('aggregation'),
  default: 'month',
})

export const request = selector<CustomFootfallRequest | undefined>({
  key: key('request'),
  get: ({ get }) => {
    const polygonId = get(customPolygonAtoms.polygonId)
    const polygonName = get(customPolygonAtoms.polygonName)

    const multipolygon = get(customPolygonAtoms.multipolygon)
    const { end } = get(dateRange)

    if (!polygonId || !polygonName || !multipolygon) {
      return undefined
    }

    const request: CustomFootfallRequest = {
      id: polygonId,
      name: polygonName,
      polygon: multipolygon,
      dataset: TRAFFIC_TRENDS_DAY_ID,
      startDate: ALL_TIME.start, // Always request all of time so we can filter down client side
      endDate: end,
    }
    return request
  },
})

export const loading = atom<boolean>({
  key: key('loading'),
  default: false,
})

export const error = atom<string | undefined>({
  key: key('error'),
  default: undefined,
})

export const response = atom<CustomFootfall<FootTrafficTrends> | undefined>({
  key: key('response'),
  default: undefined,
})

export const reportId = selector<string | undefined>({
  key: key('report-id'),
  get: ({ get }) => {
    const resp = get(response)
    return resp?.reportId
  },
})

// Aggregations for trends data
function aggregateByWeek(data: TrendsDatum[]): TrendsDatum[] {
  const result: { [key: string]: number } = {}

  data.forEach(({ value, date }) => {
    const startOfWeek = new Date(date)
    const day = startOfWeek.getDay()
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
    startOfWeek.setDate(diff)
    startOfWeek.setHours(0, 0, 0, 0) // Reset time to midnight

    const weekKey = convertDateToString(startOfWeek)

    if (!result[weekKey]) {
      result[weekKey] = 0
    }

    result[weekKey] += value
  })

  return Object.keys(result).map((date) => ({
    date,
    value: result[date],
  }))
}
function aggregateByMonth(data: TrendsDatum[]): TrendsDatum[] {
  const result: { [key: string]: number } = {}

  data.forEach(({ value, date }) => {
    const monthKey = `${getDateWithoutBloodyTZ(date).getFullYear()}-${getDateWithoutBloodyTZ(date).getMonth() + 1}`

    if (!result[monthKey]) {
      result[monthKey] = 0
    }

    result[monthKey] += value
  })

  return Object.keys(result).map((date) => ({
    date: date + '-01',
    value: result[date],
  }))
}

export const footTrafficTrends = selector<TrendsDatum[]>({
  key: key('foot-traffic-trends'),
  get: ({ get }) => {
    const resp = get(response)
    if (!resp || resp.status !== 'finished' || !resp.data) {
      return []
    }

    const data = resp.data

    // Custom date range
    const { start, end } = get(dateRange)
    const startDate = new Date(start)
    const endDate = new Date(end)

    const filterWithinDateRange = (item: (typeof data)[0]) => {
      const currentDate = new Date(item.date)
      return currentDate >= startDate && currentDate <= endDate
    }
    const chartData = data.filter(filterWithinDateRange)
    const sortedData = sortBy(chartData, (item) => localDate(item.date))
    // Fill in missing daily data (take previous day value if current day is missing/0)
    const filledData: TrendsDatum[] = []
    let index = 0
    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      const date = convertDateToString(d)
      const find = sortedData.find((d) => d.date === date)
      if (!find) {
        filledData.push({
          date,
          value: filledData[index - 1]?.value || 0,
        })
      } else {
        filledData.push(find)
      }
      index++
    }
    // Aggregation
    const aggregationType = get(aggregation)
    switch (aggregationType) {
      case 'month':
        return aggregateByMonth(filledData)
      case 'week':
        return aggregateByWeek(filledData)
      default:
        return filledData
    }
  },
})
